import { useState } from 'react';
import { Mail, Phone, MapPin, Send, Instagram } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { contactInfo, submitContactForm } from '../data/mock';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill all required fields');
      return;
    }
    setIsSubmitting(true);
    const result = await submitContactForm(formData);
    setIsSubmitting(false);
    if (result.success) {
      toast.success(result.message);
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-600 via-blue-700 to-teal-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold mb-6">
            Get in Touch
          </h1>
          <p className="text-xl text-blue-100 max-w-2xl mx-auto">
            We're here to guide you every step of the way. Reach out for a free consultation.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Contact Information */}
            <div className="lg:col-span-1 space-y-6">
              {/* Email */}
              <Card className="border-2 border-gray-200 hover:border-blue-300 transition-all">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">Email Us</h3>
                      <a 
                        href={`mailto:${contactInfo.email}`}
                        className="text-blue-600 hover:text-blue-700 break-all text-sm"
                      >
                        {contactInfo.email}
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Instagram - directly under Email */}
              <Card className="border-2 border-gray-200 hover:border-blue-300 transition-all">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-100 to-pink-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Instagram className="h-6 w-6 text-pink-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">Follow Us on Instagram</h3>
                      <a 
                        href={`https://instagram.com/${contactInfo.instagram}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-pink-600 hover:text-pink-700 text-sm font-medium"
                      >
                        @{contactInfo.instagram}
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Phone - India */}
              <Card className="border-2 border-gray-200 hover:border-blue-300 transition-all">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-orange-100 to-yellow-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Phone className="h-6 w-6 text-orange-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">India Office</h3>
                      <div className="space-y-1">
                        <a 
                          href={`tel:${contactInfo.phones.india1}`}
                          className="block text-blue-600 hover:text-blue-700 text-sm"
                        >
                          {contactInfo.phones.india1}
                        </a>
                        <a 
                          href={`tel:${contactInfo.phones.india2}`}
                          className="block text-blue-600 hover:text-blue-700 text-sm"
                        >
                          {contactInfo.phones.india2}
                        </a>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Phone - New Zealand */}
              <Card className="border-2 border-gray-200 hover:border-blue-300 transition-all">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Phone className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">New Zealand Office</h3>
                      <a 
                        href={`tel:${contactInfo.phones.newZealand}`}
                        className="text-blue-600 hover:text-blue-700 text-sm"
                      >
                        {contactInfo.phones.newZealand}
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Head Office Location */}
              <Card className="border-2 border-blue-300 bg-blue-50 hover:border-blue-400 transition-all">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-teal-600 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">Head Office (New Zealand)</h3>
                      <p className="text-gray-700 text-sm leading-relaxed">
                        {contactInfo.addresses.headOffice}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* For Feedbacks */}
              <Card className="border-2 border-gray-200 hover:border-blue-300 transition-all">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-green-100 to-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Phone className="h-6 w-6 text-green-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">For Feedbacks</h3>
                      <div className="space-y-1 text-sm">
                        <a 
                          href="tel:+64220147557"
                          className="block text-blue-600 hover:text-blue-700"
                        >
                          +64 220147557
                        </a>
                        <a 
                          href="tel:+919220537557"
                          className="block text-blue-600 hover:text-blue-700"
                        >
                          +91 9220537557
                        </a>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="border-2 border-gray-200 shadow-xl">
                <CardContent className="p-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">
                    Send Us a Message
                  </h2>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Full Name *
                        </label>
                        <Input
                          placeholder="Your name"
                          value={formData.name}
                          onChange={(e) => handleChange('name', e.target.value)}
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Email Address *
                        </label>
                        <Input
                          type="email"
                          placeholder="your.email@example.com"
                          value={formData.email}
                          onChange={(e) => handleChange('email', e.target.value)}
                          required
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Phone Number
                        </label>
                        <Input
                          type="tel"
                          placeholder="+91 XXXXX XXXXX"
                          value={formData.phone}
                          onChange={(e) => handleChange('phone', e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Subject
                        </label>
                        <Input
                          placeholder="What is this regarding?"
                          value={formData.subject}
                          onChange={(e) => handleChange('subject', e.target.value)}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Message *
                      </label>
                      <Textarea
                        placeholder="Tell us about your visa requirements or any questions you have..."
                        value={formData.message}
                        onChange={(e) => handleChange('message', e.target.value)}
                        rows={6}
                        required
                      />
                    </div>

                    <Button 
                      type="submit" 
                      size="lg" 
                      className="w-full bg-gradient-to-r from-blue-600 to-teal-600 hover:from-blue-700 hover:to-teal-700 text-white text-base"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Sending...' : 'Send Message'}
                      {!isSubmitting && <Send className="ml-2 h-5 w-5" />}
                    </Button>

                    <p className="text-center text-sm text-gray-500">
                      🔒 Your information is secure and will only be used to respond to your inquiry
                    </p>
                  </form>
                </CardContent>
              </Card>

              {/* Additional Info */}
              <div className="mt-8 bg-blue-50 border-2 border-blue-100 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-3">Prefer a Direct Call?</h3>
                <p className="text-gray-700 text-sm mb-4">
                  Our immigration consultants are available for immediate assistance. Don't hesitate to call us for urgent queries or to schedule your free consultation.
                </p>
                <div className="flex flex-wrap gap-4">
                  <a
                    href={`tel:${contactInfo.phones.newZealand}`}
                    className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-teal-600 text-white px-6 py-2.5 rounded-lg font-semibold hover:shadow-lg transition-all text-sm"
                  >
                    <Phone className="h-4 w-4" />
                    Call New Zealand Office
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
